import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import HubIcon from '@mui/icons-material/Hub';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {AdjustedRiskProbabilities} from '../../../../utils/inherent-risk-score';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';

interface SensitivityExplorerProps {
    cutSets: MinimalCutSet[];
    nodeMap: Map<number, FaultTreeNode>;
    nodeNameMap: Map<number, string>;
    totalProbability: number;
    structuralHubIds?: Set<number>;
    onAdjustedProbabilityChange?: (adjusted: AdjustedRiskProbabilities | null) => void;
}

interface BaseEventEntry {
    id: number;
    name: string;
    isBarrier: boolean;
    isHub: boolean;
    isSPF: boolean;
    originalProbability: number;
    fv: number;
    /** log10 of original probability — used as slider center */
    logP: number;
    /** slider range: [logP - 2, min(logP + 2, 0)] */
    sliderMin: number;
    sliderMax: number;
}

/** Format a probability for compact display. */
function formatProb(value: number): string {
    if (value === 0) return '0';
    if (value < 0.001) return value.toExponential(2);
    if (value >= 1) return value.toPrecision(3);
    return value.toPrecision(3);
}

/** Format a percentage change, e.g. "+12.3%" or "-5.1%". */
function formatPctChange(ratio: number): string {
    const pct = (ratio - 1) * 100;
    if (Math.abs(pct) < 0.01) return '0%';
    const sign = pct > 0 ? '+' : '';
    if (Math.abs(pct) >= 1000) return `${sign}${pct.toFixed(0)}%`;
    if (Math.abs(pct) >= 10) return `${sign}${pct.toFixed(1)}%`;
    return `${sign}${pct.toFixed(2)}%`;
}

const SLIDER_SX = {
    py: 0.5,
    '& .MuiSlider-thumb': {width: 14, height: 14},
    '& .MuiSlider-rail': {opacity: 0.3},
    '& .MuiSlider-track': {transition: 'none'},
} as const;

const SensitivityExplorer: React.FC<SensitivityExplorerProps> = ({
    cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds, onAdjustedProbabilityChange,
}) => {
    // Map of base event id → overridden log10(probability)
    const [overrides, setOverrides] = useState<Map<number, number>>(new Map());
    const [searchText, setSearchText] = useState<string>('');

    const baseEvents = useMemo((): BaseEventEntry[] => {
        if (totalProbability === 0) return [];

        const allIds = new Set<number>();
        cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

        return Array.from(allIds)
            .map((id) => {
                const node = nodeMap.get(id);
                const prob = node?.probability ?? 0;
                if (prob <= 0) return null;

                // FV for sorting
                const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));
                const containingSum = containingCS.reduce((sum, cs) => sum + cs.probability, 0);
                const fv = containingSum / totalProbability;

                const logP = Math.log10(prob);
                return {
                    id,
                    name: nodeNameMap.get(id) || `ID:${id}`,
                    isBarrier: node?.nodeClass === 'BARRIER',
                    isHub: structuralHubIds?.has(id) ?? false,
                    isSPF: containingCS.some((cs) => cs.baseEventIds.size === 1),
                    originalProbability: prob,
                    fv,
                    logP,
                    sliderMin: logP - 2,
                    sliderMax: Math.min(logP + 2, 0),
                } as BaseEventEntry;
            })
            .filter((e): e is BaseEventEntry => e !== null)
            .sort((a, b) => b.fv - a.fv);
    }, [cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds]);

    const adjustedProbs = useMemo((): AdjustedRiskProbabilities => {
        if (overrides.size === 0) return {totalProbability, order1Probability: 0, hubProbability: 0, order1to3Probability: 0};

        // Build lookup of adjusted probabilities
        const adjustedProbMap = new Map<number, number>();
        for (const [id, logVal] of overrides) {
            adjustedProbMap.set(id, Math.pow(10, logVal));
        }

        let total = 0;
        let order1 = 0;
        let hub = 0;
        let order1to3 = 0;
        for (const cs of cutSets) {
            let needsRecalc = false;
            for (const id of cs.baseEventIds) {
                if (adjustedProbMap.has(id)) {
                    needsRecalc = true;
                    break;
                }
            }
            let p: number;
            if (!needsRecalc) {
                p = cs.probability;
            } else {
                p = 1;
                for (const id of cs.baseEventIds) {
                    p *= adjustedProbMap.get(id) ?? (nodeMap.get(id)?.probability ?? 0);
                }
            }
            total += p;
            if (cs.order === 1) order1 += p;
            if (cs.order <= 3) order1to3 += p;
            if (structuralHubIds) {
                for (const id of cs.baseEventIds) {
                    if (structuralHubIds.has(id)) {
                        hub += p;
                        break;
                    }
                }
            }
        }
        return {totalProbability: total, order1Probability: order1, hubProbability: hub, order1to3Probability: order1to3};
    }, [cutSets, nodeMap, overrides, totalProbability, structuralHubIds]);

    const adjustedProbability = adjustedProbs.totalProbability;

    // Notify parent of adjusted probability changes (debounced to avoid lag during slider drag)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (!onAdjustedProbabilityChange) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onAdjustedProbabilityChange(overrides.size > 0 ? adjustedProbs : null);
        }, 150);
        return (): void => clearTimeout(debounceRef.current);
    }, [adjustedProbs, overrides.size, onAdjustedProbabilityChange]);

    /** Compute top event probability with a single event overridden to a given value. */
    const computeAdjustedForEvent = useCallback((eventId: number, newProb: number): number => {
        let total = 0;
        for (const cs of cutSets) {
            if (!cs.baseEventIds.has(eventId)) {
                total += cs.probability;
            } else {
                let p = 1;
                for (const id of cs.baseEventIds) {
                    if (id === eventId) {
                        p *= newProb;
                    } else {
                        p *= nodeMap.get(id)?.probability ?? 0;
                    }
                }
                total += p;
            }
        }
        return total;
    }, [cutSets, nodeMap]);

    /** Per-event impact: ratio of adjusted top event prob / baseline when only this event is at its slider value. */
    const eventImpacts = useMemo((): Map<number, number> => {
        const impacts = new Map<number, number>();
        for (const evt of baseEvents) {
            const logVal = overrides.get(evt.id);
            if (logVal === undefined || Math.abs(logVal - evt.logP) < 0.001) {
                impacts.set(evt.id, 1);
            } else {
                const newP = Math.pow(10, logVal);
                const adjusted = computeAdjustedForEvent(evt.id, newP);
                impacts.set(evt.id, totalProbability > 0 ? adjusted / totalProbability : 1);
            }
        }
        return impacts;
    }, [baseEvents, overrides, computeAdjustedForEvent, totalProbability]);

    const handleSliderChange = useCallback((id: number, logP: number, value: number): void => {
        setOverrides((prev) => {
            const next = new Map(prev);
            if (Math.abs(value - logP) < 0.001) {
                next.delete(id);
            } else {
                next.set(id, value);
            }
            return next;
        });
    }, []);

    const handleReset = useCallback((): void => {
        setOverrides(new Map());
    }, []);

    const overallRatio = totalProbability > 0 ? adjustedProbability / totalProbability : 1;
    const hasOverrides = overrides.size > 0;

    // Find max absolute % change for scaling the impact bars
    const maxAbsPct = useMemo((): number => {
        let max = 0;
        for (const ratio of eventImpacts.values()) {
            max = Math.max(max, Math.abs(ratio - 1));
        }
        return Math.max(max, 0.01);
    }, [eventImpacts]);

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Sensitivity analysis requires non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <Typography variant="subtitle2">What-if Probability Explorer</Typography>
                {hasOverrides && (
                    <Tooltip title="Reset all to original">
                        <IconButton size="small" onClick={handleReset}>
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* Overall probability summary */}
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ px: 2, pb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    P(top):
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatProb(totalProbability)}
                </Typography>
                {hasOverrides && (
                    <>
                        <Typography variant="body2" color="text.secondary">&rarr;</Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: overallRatio > 1.001 ? 'error.main' : overallRatio < 0.999 ? 'success.main' : 'text.primary',
                            }}
                        >
                            {formatProb(adjustedProbability)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: overallRatio > 1.001 ? 'error.main' : overallRatio < 0.999 ? 'success.main' : 'text.secondary',
                            }}
                        >
                            ({formatPctChange(overallRatio)})
                        </Typography>
                    </>
                )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, mt: 1, mb: 1 }}>
                <TextField
                    size="small"
                    placeholder="Search events"
                    value={searchText}
                    onChange={(e): void => setSearchText(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ minWidth: 150, ml: 'auto' }}
                />
            </Stack>

            {baseEvents.some((e) => e.isSPF || e.isHub) && (
                <Stack direction="row" spacing={1.5} sx={{ px: 2, pb: 0.5 }}>
                    {baseEvents.some((e) => e.isSPF) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="caption" color="text.secondary">SPF</Typography>
                        </Stack>
                    )}
                    {baseEvents.some((e) => e.isHub) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                            <Typography variant="caption" color="text.secondary">Structural hub</Typography>
                        </Stack>
                    )}
                </Stack>
            )}

            {/* Base event rows */}
            <Box sx={{ maxHeight: 400, overflow: 'auto', px: 2, pb: 1 }}>
                {baseEvents.filter((evt) => !searchText.trim() || evt.name.toLowerCase().includes(searchText.trim().toLowerCase())).map((evt) => {
                    const currentLog = overrides.get(evt.id) ?? evt.logP;
                    const currentProb = Math.pow(10, currentLog);
                    const impact = eventImpacts.get(evt.id) ?? 1;
                    const pctChange = (impact - 1);
                    const barWidth = maxAbsPct > 0 ? Math.abs(pctChange) / maxAbsPct * 50 : 0;
                    const isModified = overrides.has(evt.id);

                    return (
                        <Box key={evt.id} sx={{ mb: 0.5 }}>
                            {/* Event name row */}
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ maxWidth: '55%' }}>
                                    {evt.isSPF && (
                                        <Tooltip title="Single point of failure" arrow>
                                            <LooksOneIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />
                                        </Tooltip>
                                    )}
                                    {evt.isHub && (
                                        <Tooltip title="Structural hub (appears in ≥50% of cut sets)" arrow>
                                            <HubIcon sx={{ fontSize: 14, color: '#ed6c02', flexShrink: 0 }} />
                                        </Tooltip>
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: isModified ? 600 : 400,
                                            color: evt.isBarrier ? 'success.main' : 'text.primary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={evt.name}
                                    >
                                        {evt.name}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                        {formatProb(currentProb)}
                                    </Typography>
                                    {/* Impact bar */}
                                    <Box sx={{ width: 60, height: 12, position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        {/* Center line */}
                                        <Box sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            width: '1px',
                                            height: '100%',
                                            bgcolor: 'divider',
                                        }} />
                                        {/* Bar */}
                                        {Math.abs(pctChange) > 0.0001 && (
                                            <Box sx={{
                                                position: 'absolute',
                                                left: pctChange > 0 ? '50%' : `${50 - barWidth}%`,
                                                width: `${barWidth}%`,
                                                height: 8,
                                                borderRadius: 0.5,
                                                bgcolor: pctChange > 0 ? 'error.light' : 'success.light',
                                                transition: 'none',
                                            }} />
                                        )}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.65rem',
                                            width: 48,
                                            textAlign: 'right',
                                            color: pctChange > 0.0001 ? 'error.main' : pctChange < -0.0001 ? 'success.main' : 'text.disabled',
                                        }}
                                    >
                                        {isModified ? formatPctChange(impact) : ''}
                                    </Typography>
                                </Stack>
                            </Stack>
                            {/* Slider row */}
                            <Slider
                                value={currentLog}
                                min={evt.sliderMin}
                                max={evt.sliderMax}
                                step={0.01}
                                onChange={(_e, val): void => handleSliderChange(evt.id, evt.logP, val as number)}
                                size="small"
                                sx={{
                                    ...SLIDER_SX,
                                    color: isModified ? (pctChange > 0 ? 'error.main' : 'success.main') : 'primary.main',
                                }}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v: number): string => formatProb(Math.pow(10, v))}
                                marks={[{value: evt.logP, label: ''}]}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default SensitivityExplorer;
